import React, { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { ModelInfo } from '../types/models';
import { ApiService } from '../services/apiService';
import { modelDisplayLabel } from '../utils/modelCostLabel';
import { useViewMode } from '../hooks/useViewMode';

export interface JudgeModelSelection {
  providerType: string;
  modelId: string;
  modelInfo?: ModelInfo;
}

interface JudgeModelSelectorProps {
  providerType: string;
  modelId: string;
  onChange: (selection: JudgeModelSelection) => void;
  disabled?: boolean;
  providerError?: string;
  modelError?: string;
}

/**
 * Reusable provider + model selector backed by the available models API.
 * Renders two dropdowns: provider type and model ID.
 */
export const JudgeModelSelector: React.FC<JudgeModelSelectorProps> = ({
  providerType,
  modelId,
  onChange,
  disabled = false,
  providerError,
  modelError,
}) => {
  const [availableModels, setAvailableModels] = useState<Record<string, ModelInfo[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { viewMode } = useViewMode();
  const showCost = viewMode === 'admin';

  useEffect(() => {
    const load = async () => {
      try {
        const apiService = ApiService.getInstance();
        const data = await apiService.getAvailableModels();
        setAvailableModels(data.modelsByProvider || {});
      } catch (err: any) {
        console.error('Failed to load available models:', err);
        setError(err.message || 'Failed to load models');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const providerTypes = Object.keys(availableModels);
  const models = providerType ? (availableModels[providerType] || []) : [];

  const handleProviderChange = (newProvider: string) => {
    onChange({ providerType: newProvider, modelId: '', modelInfo: undefined });
  };

  const handleModelChange = (newModelId: string) => {
    const selectedModel = models.find(m => m.modelId === newModelId);
    onChange({ providerType, modelId: newModelId, modelInfo: selectedModel });
  };

  if (loading) {
    return (
      <div data-component="JudgeModelSelector" className="flex items-center gap-2 text-gray-500 text-sm py-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading models...
      </div>
    );
  }

  if (error) {
    return (
      <div data-component="JudgeModelSelector" className="text-sm text-red-600 py-2">
        Failed to load models: {error}
      </div>
    );
  }

  return (
    <div data-component="JudgeModelSelector" className="grid grid-cols-2 gap-4">
      <div>
        <label htmlFor="judgeModelProvider" className="block text-sm font-medium text-gray-700 mb-1">
          Provider *
        </label>
        <select
          id="judgeModelProvider"
          value={providerType}
          onChange={(e) => handleProviderChange(e.target.value)}
          disabled={disabled}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
            providerError ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select provider...</option>
          {providerTypes.map((provider) => (
            <option key={provider} value={provider}>
              {provider}
            </option>
          ))}
        </select>
        {providerError && (
          <p className="text-sm text-red-600 mt-1">{providerError}</p>
        )}
      </div>

      <div>
        <label htmlFor="judgeModelId" className="block text-sm font-medium text-gray-700 mb-1">
          Model *
        </label>
        <select
          id="judgeModelId"
          value={modelId}
          onChange={(e) => handleModelChange(e.target.value)}
          disabled={disabled || !providerType}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 ${
            modelError ? 'border-red-500' : 'border-gray-300'
          }`}
        >
          <option value="">Select model...</option>
          {models.map((model) => (
            <option key={model.modelId} value={model.modelId}>
              {modelDisplayLabel(model, showCost)}
            </option>
          ))}
        </select>
        {modelError && (
          <p className="text-sm text-red-600 mt-1">{modelError}</p>
        )}
      </div>
    </div>
  );
};
